<!-- warehouse_scan/public/js/components/ScanResult.vue -->
<template>
  <div class="scan-result" :class="{ success: result.success, error: !result.success }">
    <!-- 结果头部 -->
    <div class="result-header">
      <div class="result-icon" :class="{ success: result.success }">
        {{ result.success ? '✓' : '✗' }}
      </div>
      <div class="result-info">
        <div class="result-title">{{ result.success ? '扫描成功' : '扫描失败' }}</div>
        <div class="result-time">{{ formatTime }}</div>
      </div>
      <button class="close-btn" @click="$emit('confirm')" title="关闭">✕</button>
    </div>

    <!-- 结果内容 -->
    <div class="result-content">
      <!-- 托盘结果 -->
      <template v-if="result.data?.level === 'PALLET'">
        <div class="info-group">
          <div class="info-row">
            <span class="label">托盘码</span>
            <span class="value code">{{ result.data.pallet_code }}</span>
          </div>
          <div class="info-row">
            <span class="label">总箱数</span>
            <span class="value">{{ result.data.total_cases || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">总数量</span>
            <span class="value highlight">{{ result.data.total_quantity || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">状态</span>
            <span class="value status-badge" :class="getStatusClass(result.data.status)">
              {{ getStatusText(result.data.status) }}
            </span>
          </div>
        </div>

        <!-- 箱明细 -->
        <div v-if="result.data.cases?.length" class="detail-section">
          <div class="section-title">
            <span>📋 箱明细</span>
            <span class="section-count">{{ result.data.cases.length }}箱</span>
          </div>
          <div class="case-list">
            <div v-for="c in result.data.cases.slice(0, 5)" :key="c.case_code" class="case-item">
              <span class="case-code">{{ c.case_code }}</span>
              <span class="case-item-code">{{ c.item_code }}</span>
              <span class="case-qty">{{ c.quantity }}</span>
            </div>
            <div v-if="result.data.cases.length > 5" class="more-text">
              等{{ result.data.cases.length }}箱
            </div>
          </div>
        </div>
      </template>

      <!-- 箱子结果 -->
      <template v-else-if="result.data?.level === 'CASE'">
        <div class="info-group">
          <div class="info-row">
            <span class="label">箱码</span>
            <span class="value code">{{ result.data.case_code }}</span>
          </div>
          <div class="info-row">
            <span class="label">物料</span>
            <span class="value">{{ result.data.item_code }}</span>
          </div>
          <div class="info-row">
            <span class="label">批次</span>
            <span class="value">{{ result.data.batch_no || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="label">数量</span>
            <span class="value highlight">{{ result.data.quantity || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="label">可用</span>
            <span class="value">{{ result.data.available || result.data.quantity || 0 }}</span>
          </div>
        </div>

        <!-- 序列号 -->
        <div v-if="result.data.serials?.length" class="detail-section">
          <div class="section-title">
            <span>🔖 序列号</span>
            <span class="section-count">{{ result.data.serials.length }}个</span>
          </div>
          <div class="serials-list">
            {{ result.data.serials.slice(0, 3).join(', ') }}
            <span v-if="result.data.serials.length > 3">等{{ result.data.serials.length }}个</span>
          </div>
        </div>
      </template>

      <!-- 物料结果 -->
      <template v-else-if="result.data?.level === 'ITEM'">
        <div class="info-group">
          <div class="info-row">
            <span class="label">物料</span>
            <span class="value code">{{ result.data.item_code }}</span>
          </div>
          <div class="info-row">
            <span class="label">名称</span>
            <span class="value">{{ result.data.item_name || '-' }}</span>
          </div>
          <div class="info-row">
            <span class="label">单位</span>
            <span class="value">{{ result.data.stock_uom || '-' }}</span>
          </div>
        </div>

        <!-- 库存分布 -->
        <div v-if="result.data.stock_balance?.length" class="detail-section">
          <div class="section-title">📦 库存分布</div>
          <div class="stock-list">
            <div v-for="s in result.data.stock_balance" :key="s.warehouse" class="stock-item">
              <span class="stock-warehouse">{{ s.warehouse }}</span>
              <span class="stock-qty">{{ s.actual_qty }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- 库位结果 -->
      <template v-else-if="result.data?.level === 'LOCATION'">
        <div class="info-group">
          <div class="info-row">
            <span class="label">库位</span>
            <span class="value location-value">{{ result.data.warehouse }}</span>
          </div>
        </div>
      </template>

      <!-- 错误信息 -->
      <div v-if="!result.success && result.message" class="error-message">
        <span class="error-icon">⚠️</span>
        {{ result.message }}
      </div>

      <!-- 警告信息 -->
      <div v-if="result.warning" class="warning-message">
        <span class="warning-icon">⚠️</span>
        {{ result.warning }}
      </div>

      <!-- 创建的单据 -->
      <div v-if="result.created_document" class="doc-created">
        <span class="doc-icon">✅</span>
        <span>已创建单据: {{ result.created_document }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  result: {
    type: Object,
    required: true
  }
});

defineEmits(['confirm']);

const formatTime = computed(() => {
  return new Date().toLocaleTimeString('zh-CN');
});

const getStatusClass = (status) => {
  const classes = {
    'Received': 'success',
    'Shipped': 'info',
    'In Stock': 'primary',
    'Partially Opened': 'warning'
  };
  return classes[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    'Received': '已收货',
    'Shipped': '已发货',
    'In Stock': '在库',
    'Partially Opened': '部分开封',
    'Created': '已创建',
    'Produced': '已生产'
  };
  return texts[status] || status || '未知';
};
</script>

<style scoped>
.scan-result {
  background: white;
  border-radius: 12px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

.scan-result.success {
  border-left: 4px solid #28a745;
}

.scan-result.error {
  border-left: 4px solid #dc3545;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.result-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  background: #f8f9fa;
  border-bottom: 1px solid #f1f3f5;
  position: relative;
}

.result-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  background: #e9ecef;
  color: #6c757d;
}

.result-icon.success {
  background: #d4edda;
  color: #28a745;
}

.result-info {
  flex: 1;
}

.result-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 2px;
}

.result-time {
  font-size: 12px;
  color: #6c757d;
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #6c757d;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}

.close-btn:hover {
  background: #e9ecef;
}

.result-content {
  padding: 15px;
}

.info-group {
  margin-bottom: 15px;
}

.info-row {
  display: flex;
  padding: 8px 0;
  border-bottom: 1px dashed #f1f3f5;
}

.info-row .label {
  width: 70px;
  color: #6c757d;
  font-size: 13px;
}

.info-row .value {
  flex: 1;
  font-weight: 500;
  color: #2c3e50;
}

.info-row .value.code {
  font-family: monospace;
  color: #007bff;
}

.info-row .value.highlight {
  color: #28a745;
  font-weight: 600;
}

.location-value {
  color: #28a745;
  font-weight: 600;
}

.status-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: normal;
}

.status-badge.success {
  background: #d4edda;
  color: #155724;
}

.status-badge.info {
  background: #d1ecf1;
  color: #0c5460;
}

.status-badge.primary {
  background: #cce5ff;
  color: #004085;
}

.status-badge.warning {
  background: #fff3cd;
  color: #856404;
}

.status-badge.default {
  background: #e2e3e5;
  color: #383d41;
}

.detail-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f1f3f5;
}

.section-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 13px;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-count {
  font-size: 11px;
  color: #6c757d;
  font-weight: normal;
}

.case-list {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 8px;
}

.case-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 8px;
  font-size: 13px;
  border-bottom: 1px solid #dee2e6;
}

.case-item:last-child {
  border-bottom: none;
}

.case-code {
  font-family: monospace;
  color: #007bff;
}

.case-item-code {
  flex: 1;
  margin: 0 10px;
  color: #6c757d;
}

.case-qty {
  font-weight: 600;
  color: #28a745;
}

.more-text {
  text-align: center;
  color: #6c757d;
  font-size: 12px;
  padding-top: 6px;
}

.serials-list {
  background: #e8f5e9;
  border-radius: 8px;
  padding: 10px;
  font-size: 12px;
  color: #2e7d32;
  word-break: break-all;
}

.stock-list {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 8px;
}

.stock-item {
  display: flex;
  justify-content: space-between;
  padding: 6px 8px;
  font-size: 13px;
  border-bottom: 1px solid #dee2e6;
}

.stock-item:last-child {
  border-bottom: none;
}

.stock-warehouse {
  color: #495057;
}

.stock-qty {
  font-weight: 600;
  color: #007bff;
}

.error-message, .warning-message {
  padding: 10px;
  border-radius: 8px;
  font-size: 13px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
}

.warning-message {
  background: #fff3cd;
  color: #856404;
}

.doc-created {
  margin-top: 12px;
  padding: 10px;
  background: #d4edda;
  border-radius: 8px;
  color: #155724;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
</style>